/**
 * System Error Translation Utility
 *
 * Converts system/infrastructure errors (database, network, API) into generic
 * user-friendly messages while preserving technical details in console logs.
 *
 * User errors (validation, business logic) are NOT translated and are preserved as-is.
 */

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  CRITICAL = 'critical', // Blocks all functionality, requires immediate attention
  BLOCKING = 'blocking', // Blocks specific workflow, requires user action
  NON_BLOCKING = 'non-blocking' // Doesn't block workflow, informational
}

/**
 * Error categories
 */
export enum ErrorCategory {
  SYSTEM = 'system', // Infrastructure errors (database, network, API)
  USER_ACTION = 'user-action', // Errors from user actions (add/remove, create/update)
  VALIDATION = 'validation', // Form validation errors
  DATA_LOADING = 'data-loading' // Data fetching/loading errors
}

/**
 * Recovery actions available for errors
 */
export interface RecoveryActions {
  retry?: () => void | Promise<void>;
  dismiss?: () => void;
  acknowledge?: () => void;
}

/**
 * Error metadata returned by error classification
 */
export interface ErrorMetadata {
  severity: ErrorSeverity;
  category: ErrorCategory;
  message: string;
  recoveryActions?: RecoveryActions;
  userMessage: string; // Human-readable message
  technicalDetails?: unknown; // Technical details for logging
}

/**
 * Generic system error message shown to users
 */
const SYSTEM_ERROR_MESSAGE =
  'A system error occurred. Please contact support if the problem persists.';

/**
 * Supabase error codes that indicate system errors
 */
const SUPABASE_SYSTEM_ERROR_CODES = [
  '23503', // Foreign key constraint violation
  '23502', // Not null constraint violation
  '42P17', // RLS policy recursion
  'PGRST116', // Not found (no rows returned)
  'PGRST301', // JWT expired
  '42501', // Insufficient privileges
  '57014' // Query canceled (statement timeout)
];

/**
 * Checks if an error is a system/infrastructure error
 * @param error - The error to check
 * @returns true if the error is a system error, false otherwise
 */
// eslint-disable-next-line complexity
export function isSystemError(error: unknown): boolean {
  if (!error) return false;

  // Check if it's a Supabase error (has code property)
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const errorCode = (error as { code?: string }).code;
    if (errorCode && SUPABASE_SYSTEM_ERROR_CODES.includes(errorCode)) {
      return true;
    }
    // Any error with a code property from Supabase is likely a system error
    // (except 23505 which is handled as user error in some contexts)
    if (errorCode && typeof errorCode === 'string') {
      return true;
    }
  }

  // Check if it's a network error
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // Check if it's a Response error (API errors)
  if (error instanceof Response) {
    const status = error.status;
    if (status >= 500 && status < 600) {
      return true; // Server errors
    }
  }

  // Check if error message indicates system error
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    if (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('database') ||
      message.includes('constraint') ||
      message.includes('violates') ||
      message.includes('pg_') ||
      message.includes('pgrst')
    ) {
      return true;
    }
  }

  return false;
}

/**
 * Logs system error details to console for debugging
 * @param error - The error to log
 * @param context - Optional context about where the error occurred
 */
export function logSystemError(error: unknown, context?: string): void {
  const timestamp = new Date().toISOString();
  const errorInfo: Record<string, unknown> = {
    timestamp,
    context: context || 'unknown operation'
  };

  if (error instanceof Error) {
    errorInfo.message = error.message;
    errorInfo.stack = error.stack;
  }

  if (typeof error === 'object' && error !== null) {
    if ('code' in error) {
      errorInfo.code = (error as { code?: unknown }).code;
    }
    if ('details' in error) {
      errorInfo.details = (error as { details?: unknown }).details;
    }
    if ('hint' in error) {
      errorInfo.hint = (error as { hint?: unknown }).hint;
    }
  }

  console.error('[System Error]', errorInfo);
}

/**
 * Translates a system error into a generic user-friendly message
 * @param error - The error to translate
 * @param context - Optional context about where the error occurred
 * @returns A new Error with the generic system error message
 */
export function translateSystemError(error: unknown, context?: string): Error {
  // Log technical details for debugging
  logSystemError(error, context);

  // Provide a specific message for statement timeout errors
  if (typeof error === 'object' && error !== null && 'code' in error) {
    if ((error as { code?: string }).code === '57014') {
      return new Error('The operation took too long. Please try again shortly.');
    }
  }

  // Return generic user message
  return new Error(SYSTEM_ERROR_MESSAGE);
}

/**
 * Classifies and translates an error into error metadata
 * @param error - The error to classify
 * @param context - Optional context about where the error occurred
 * @param category - Error category (defaults to SYSTEM if isSystemError is true)
 * @param recoveryActions - Optional recovery actions
 * @returns Error metadata with classification and user-friendly message
 */
// eslint-disable-next-line max-lines-per-function
export function classifyError(
  error: unknown,
  context?: string,
  category?: ErrorCategory,
  recoveryActions?: RecoveryActions
): ErrorMetadata {
  const isSystem = isSystemError(error);
  const errorCategory = category || (isSystem ? ErrorCategory.SYSTEM : ErrorCategory.USER_ACTION);

  // Determine severity based on error type and category
  let severity: ErrorSeverity = ErrorSeverity.BLOCKING;

  if (errorCategory === ErrorCategory.VALIDATION) {
    severity = ErrorSeverity.NON_BLOCKING;
  } else if (errorCategory === ErrorCategory.DATA_LOADING) {
    // Data loading errors are blocking but not critical
    severity = ErrorSeverity.BLOCKING;
  } else if (isSystem) {
    // System errors are critical if they're 500+ or fatal
    if (error instanceof Response) {
      const status = error.status;
      if (status >= 500) {
        severity = ErrorSeverity.CRITICAL;
      } else {
        severity = ErrorSeverity.BLOCKING;
      }
    } else if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (
        message.includes('fatal') ||
        message.includes('critical') ||
        message.includes('unrecoverable')
      ) {
        severity = ErrorSeverity.CRITICAL;
      }
    }
  } else {
    // User action errors are typically blocking
    severity = ErrorSeverity.BLOCKING;
  }

  // Get user-friendly message
  let userMessage: string;
  if (isSystem) {
    userMessage = SYSTEM_ERROR_MESSAGE;
    logSystemError(error, context);
  } else if (error instanceof Error) {
    userMessage = error.message;
  } else {
    userMessage = 'An error occurred. Please try again.';
  }

  // Get technical details for logging
  let technicalDetails: unknown = error;
  if (error instanceof Error) {
    technicalDetails = {
      message: error.message,
      stack: error.stack,
      name: error.name
    };
  }

  return {
    severity,
    category: errorCategory,
    message: userMessage,
    recoveryActions,
    userMessage,
    technicalDetails
  };
}

/**
 * Helper to create error metadata for data loading errors
 */
export function createDataLoadingError(
  error: unknown,
  context?: string,
  retry?: () => void | Promise<void>
): ErrorMetadata {
  return classifyError(error, context, ErrorCategory.DATA_LOADING, retry ? { retry } : undefined);
}

/**
 * Helper to create error metadata for user action errors
 */
export function createUserActionError(error: unknown, context?: string): ErrorMetadata {
  return classifyError(error, context, ErrorCategory.USER_ACTION);
}

/**
 * Helper to create error metadata for validation errors
 */
export function createValidationError(message: string): ErrorMetadata {
  return {
    severity: ErrorSeverity.NON_BLOCKING,
    category: ErrorCategory.VALIDATION,
    message,
    userMessage: message
  };
}
